import { MouseEvent, TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PopoverActions } from '@material-ui/core/Popover';

import { isTouchDevice as getIsTouchDevice } from '../../utils';
import { PopoverControlsProps } from './Popover.types';

export const usePopoverControls = ({
  variant,
  open,
  disabled,
  disableClickAway,
  popoverProps,
  toggleProps,
  onOpen,
  onClose,
}: PopoverControlsProps) => {
  const popoverAnchor = useRef<HTMLElement | null>(null);
  const popoverActions = useRef<PopoverActions | null>(null);
  const isPopoverContentClickedRef = useRef(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isHoverVariant = useMemo(() => variant === 'hover', [variant]);

  const isTouchDevice = useMemo(() => getIsTouchDevice(), []);

  const popoverOpen = useCallback(() => {
    if (onOpen) {
      onOpen();
    }

    if (typeof open !== 'boolean') {
      setIsOpen(true);
    }
  }, [open, onOpen]);

  const popoverClose = useCallback(() => {
    if (onClose) {
      onClose();
    }

    if (typeof open !== 'boolean') {
      setIsOpen(false);
    }
  }, [open, onClose]);

  const handleToggleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      if (!isHoverVariant) {
        if (!isOpen) {
          popoverOpen();
        } else {
          popoverClose();
        }
      }

      if (toggleProps?.onClick) {
        toggleProps.onClick(e);
      }
    },
    [disabled, isHoverVariant, isOpen, toggleProps?.onClick, popoverOpen, popoverClose],
  );

  const handleToggleMouseEnter = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      popoverOpen();

      if (toggleProps?.onMouseEnter) {
        toggleProps.onMouseEnter(e);
      }
    },
    [disabled, toggleProps?.onMouseEnter, popoverOpen],
  );

  const handleToggleMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      popoverClose();

      if (toggleProps?.onMouseLeave) {
        toggleProps.onMouseLeave(e);
      }
    },
    [disabled, toggleProps?.onMouseLeave, popoverClose],
  );

  const handleToggleClickAway = useCallback(() => {
    if (disabled || isHoverVariant || disableClickAway) {
      return;
    }

    if (isPopoverContentClickedRef.current) {
      isPopoverContentClickedRef.current = false;
    } else {
      popoverClose();
    }
  }, [disabled, isHoverVariant, disableClickAway, popoverClose]);

  const handlePopoverClick = useCallback(
    (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      if (!isHoverVariant) {
        isPopoverContentClickedRef.current = true;
      }

      if (isTouchDevice) {
        if (popoverProps?.PaperProps?.onTouchEnd) {
          popoverProps.PaperProps.onTouchEnd(e as TouchEvent<HTMLDivElement>);
        }
      } else {
        if (popoverProps?.PaperProps?.onClick) {
          popoverProps.PaperProps.onClick(e as MouseEvent<HTMLDivElement>);
        }
      }
    },
    [disabled, isHoverVariant, isTouchDevice, popoverProps?.PaperProps?.onClick, popoverProps?.PaperProps?.onTouchEnd],
  );

  const handlePopoverMouseEnter = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      popoverOpen();

      if (popoverProps?.PaperProps?.onMouseEnter) {
        popoverProps.PaperProps.onMouseEnter(e);
      }
    },
    [disabled, popoverProps?.PaperProps?.onMouseEnter, popoverOpen],
  );

  const handlePopoverMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      popoverClose();

      if (popoverProps?.PaperProps?.onMouseLeave) {
        popoverProps.PaperProps.onMouseLeave(e);
      }
    },
    [disabled, popoverProps?.PaperProps?.onMouseLeave, popoverClose],
  );

  const toggleTriggerProps = useMemo(
    () => ({
      ...(isHoverVariant && { onMouseEnter: handleToggleMouseEnter, onMouseLeave: handleToggleMouseLeave }),
      onClick: handleToggleClick,
    }),
    [isHoverVariant, handleToggleMouseEnter, handleToggleMouseLeave, handleToggleClick],
  );

  const popoverTriggerProps = useMemo(
    () => ({
      ...(isHoverVariant && { onMouseEnter: handlePopoverMouseEnter, onMouseLeave: handlePopoverMouseLeave }),
      ...(isTouchDevice ? { onTouchEnd: handlePopoverClick } : { onClick: handlePopoverClick }),
    }),
    [isHoverVariant, isTouchDevice, handlePopoverMouseEnter, handlePopoverMouseLeave, handlePopoverClick],
  );

  useEffect(() => {
    if (!disabled && typeof open === 'boolean') {
      setIsOpen(open);
    }
  }, [disabled, open]);

  useEffect(() => {
    if (!isOpen || !popoverAnchor.current) {
      return;
    }

    const anchor = popoverAnchor.current;

    const updatePosition = () => {
      if (popoverActions.current) {
        popoverActions.current.updatePosition();
      }
    };

    const scrollableAncestors: (Element | Window)[] = [window];
    let parent = anchor.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;

      if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
        scrollableAncestors.push(parent);
      }

      parent = parent.parentElement;
    }

    scrollableAncestors.forEach(el => {
      el.addEventListener('scroll', updatePosition, { passive: true });
    });

    return () => {
      scrollableAncestors.forEach(el => {
        el.removeEventListener('scroll', updatePosition);
      });
    };
  }, [isOpen]);

  return {
    isOpen,
    isHoverVariant,
    popoverAnchor,
    popoverActions,
    toggleTriggerProps,
    popoverTriggerProps,
    popoverOpen,
    popoverClose,
    handleToggleClickAway,
  };
};
